import {fabric} from 'fabric';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useInMwEditDesignButtonAbTest} from '~/common/hooks/useInMwEditDesignButtonAbTest';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {canvasHideTextBorder} from '~/common/canvas/customs/text_border';
import {
  UPDATE_DESIGN_OPTIONS,
  UPDATE_PRISTINE_UYO,
} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {selectPristineUyo} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {UploadButton} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/CardEditor/actions/UploadButton/UploadButton';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {deriveIdFromCanvasObject} from '~/CreateFabricInvitation/utils/canvas';
import {useShadowImage} from './hooks/useShadowImage';

import './Uyo.sass';

export const Uyo = ({btnText = '', nodeRef, index, id}) => {
  const {canvas} = useContext(EventCreateContext);
  const [styles, setStyles] = useState({});
  const pristineUyo = useSelector(selectPristineUyo);

  useShadowImage(canvas);

  let uyoImage;
  const dispatch = useDispatch();
  const selectUyo = useCallback(
    (e) => {
      uyoImage = canvas
        .getObjects()
        .find((obj) => obj.type === 'uYOImage' && (obj.id || deriveIdFromCanvasObject(obj)) === id);
      const rect = canvas
        .getObjects()
        .find((obj) => obj.type === 'rect' && (obj.id || deriveIdFromCanvasObject(obj)) === id);
      const activeTextboxes = canvas.getActiveObjects().filter((obj) => obj.type === 'textbox');
      const mousePosition = canvas.getPointer(e);
      const texts = canvas.getObjects().filter(({type}) => type === 'textbox');
      const pointer = canvas.getPointer(e, true);
      // eslint-disable-next-line no-underscore-dangle
      const target = canvas._searchPossibleTargets(texts, pointer);
      if (target) {
        canvas.setActiveObject(target).renderAll();
        canvasHideTextBorder(target, canvas);
      } else if (
        uyoImage &&
        uyoImage.selectable !== false &&
        rect &&
        rect.containsPoint(mousePosition, null, true) &&
        activeTextboxes.length === 0
      ) {
        canvas.setActiveObject(uyoImage).renderAll();
      }
    },
    [canvas]
  );

  const positionBtn = useCallback((obj, theCanvas) => {
    const {x: left, y: top} = obj.getCenterPoint();
    const zoom = theCanvas.getZoom();

    setStyles({
      left: `${left * zoom}px`,
      top: `${top * zoom - 20}px`,
      display: 'flex',
    });
  }, []);

  const overrideFindTarget = useCallback(() => {
    fabric.util.addListener(canvas.upperCanvasEl, 'mousedown', selectUyo);
    fabric.util.addListener(canvas.upperCanvasEl, 'touchstart', selectUyo);

    /* eslint-disable no-underscore-dangle */
    canvas.findTarget = (e, skipGroup) => {
      if (canvas.skipTargetFind) {
        return undefined;
      }
      const ignoreZoom = true;
      const pointer = canvas.getPointer(e, ignoreZoom);
      const activeObject = canvas._activeObject;
      const aObjects = canvas.getActiveObjects();
      let activeTarget;
      let activeTargetSubs;
      const isTouch = fabric.util.isTouchEvent(e);
      const shouldLookForActive = (aObjects.length > 1 && !skipGroup) || aObjects.length === 1;

      canvas.targets = [];

      if (shouldLookForActive && activeObject._findTargetCorner(pointer, isTouch)) {
        return activeObject;
      }

      if (
        aObjects.length > 1 &&
        !skipGroup &&
        activeObject === canvas._searchPossibleTargets([activeObject], pointer)
      ) {
        return activeObject;
      }
      if (aObjects.length === 1 && !!canvas._searchPossibleTargets([activeObject], pointer)) {
        return activeObject;
      }
      let target = canvas._searchPossibleTargets(canvas._objects, pointer);
      if (e[canvas.altSelectionKey] && target && activeTarget && target !== activeTarget) {
        target = activeTarget;
        canvas.targets = activeTargetSubs;
      }
      return target;
    };
    /* eslint-enable no-underscore-dangle */
  }, [canvas]);

  const imageUploadedCallback = useCallback(
    (image) => {
      overrideFindTarget();
      dispatch({
        type: UPDATE_PRISTINE_UYO,
        payload: {pristineUyo: false, id},
      });
      canvas.setActiveObject(image).renderAll();
    },
    [canvas, selectUyo]
  );

  useEffect(() => {
    if (canvas) {
      uyoImage = canvas
        .getObjects()
        .find((obj) => obj.type === 'uYOImage' && (obj.id || deriveIdFromCanvasObject(obj)) === id);

      const rect = canvas
        .getObjects()
        .find((obj) => obj.type === 'rect' && (obj.id || deriveIdFromCanvasObject(obj)) === id);
      const textbox = canvas.getObjects().filter((obj) => obj.type === 'textbox');

      const positionBtnHandler = () => positionBtn(uyoImage, canvas);
      if ((!rect && uyoImage) || uyoImage?.isError) {
        positionBtnHandler();
        window.addEventListener('resize', positionBtnHandler);
        dispatch({
          type: UPDATE_PRISTINE_UYO,
          payload: {pristineUyo: true, id},
        });
      } else if (rect && uyoImage) {
        uyoImage.hasControls = true;
        // TODO: Do we need this?
        // uyoImage._originalObject = rect.toJSON();
        overrideFindTarget();
      }

      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {hasTextbox: !!textbox.length},
      });

      return () => {
        window.removeEventListener('resize', positionBtnHandler);
      };
    }
    return () => {};
  }, [!!canvas, canvas?.getObjects()?.length, id]);

  const {inIsolatedEditMode} = useContext(FabricContext);
  const isInEditButtonTest = useInMwEditDesignButtonAbTest();

  const showUyoButton =
    pristineUyo[id] === true && (!isInEditButtonTest || (isInEditButtonTest && inIsolatedEditMode));

  return showUyoButton ? (
    <UploadButton
      btnText={btnText}
      styles={styles}
      className="uyo__action__button"
      callback={imageUploadedCallback}
      ref={nodeRef}
      id={id}
      index={index}
    />
  ) : null;
};
